// Cliente HTTP de la "Memoria aprendida" de la IA del helpdesk
// (helpdesk/ai-memory): datos generales por courier que la IA usa al responder.
//
// Calca el patrón de aiActionsApi.js: mismo BFF, mismos headers, mismos errores
// tipados con `.status` para poder distinguir "ruta aún no desplegada"
// (isEndpointMissing, re-exportado desde aiSettingsApi — no se duplica) de un
// error real.
//
// El id de un fact viaja en la URL: se codifica con encodeURIComponent para que
// un id con "/" o "?" no termine apuntando a otra ruta. Prohibido loguear
// payloads acá.

import { isEndpointMissing } from "./aiSettingsApi";

// Typed error shared by every helpdesk client (status, code y el mensaje de
// negocio que apiErrorMessage sabe leer de vuelta).
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// Re-export: las vistas importan todo desde este módulo, pero la implementación
// vive en un solo lugar (aiSettingsApi).
export { isEndpointMissing };

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

const MEMORY_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/ai-memory`;

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

function factPath(id) {
  return `${MEMORY_PATH}/${encodeURIComponent(id)}`;
}

/**
 * Datos de la memoria para una vista. Contrato:
 * { ok, facts: Fact[], counts: { pending, active, disabled, rejected,
 *   superseded }, active_chars, truncated }.
 * `status` se OMITE del querystring cuando no viene: un `status=` vacío no es
 * lo mismo que "sin filtro" y el backend puede leerlo como un valor inválido.
 * @param {{status?: 'pending'|'active'|'disabled'}} [filters]
 * @returns {Promise<object>}
 */
export async function getAiMemory({ status } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  const suffix = qs.toString() ? `?${qs}` : "";

  const response = await authFetch(`${MEMORY_PATH}${suffix}`, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Agrega un dato a mano (queda active). Contrato: { ok, fact }.
 * Límites del backend: topic ≤ 80, content ≤ 600, audiences ≥ 1.
 * @param {{topic: string, content: string, audiences: string[]}} payload
 * @returns {Promise<object>}
 */
export async function createAiMemoryFact({ topic, content, audiences }) {
  const response = await authFetch(MEMORY_PATH, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ topic, content, audiences }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Revisa o edita un dato. `body` es O una acción de revisión
 * (`{ action: 'approve'|'reject'|'disable'|'enable' }`) O el contenido editado
 * (`{ topic?, content?, audiences? }`), nunca ambos. Contrato: { ok, fact }.
 * @param {string} id
 * @param {object} body
 * @returns {Promise<object>}
 */
export async function updateAiMemoryFact(id, body) {
  const response = await authFetch(factPath(id), {
    method: "PATCH",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Borra un dato de forma definitiva. Un 204 sin body también resuelve como
 * { ok: true }: parsear JSON ahí tira SyntaxError.
 * @param {string} id
 * @returns {Promise<{ok: boolean}>}
 */
export async function deleteAiMemoryFact(id) {
  const response = await authFetch(factPath(id), {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  if (response.status === 204) return { ok: true };
  return response.json().catch(() => ({ ok: true }));
}

/**
 * Aprendizaje de consultas pasadas (202: corre en segundo plano y lo que
 * aprende queda pendiente de revisión). Contrato: { ok, started, days,
 * candidates, skipped_no_agent }.
 * @param {{days?: number}} [options]
 * @returns {Promise<object>}
 */
export async function runAiMemoryBackfill({ days = 30 } = {}) {
  const response = await authFetch(`${MEMORY_PATH}/backfill`, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ days }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}
